/** A cell's place in a grid: its column (A is 1) and row, both 1-based. */
export interface CellPosition {
  column: number;
  row: number;
}
/**
 * A rectangle of cells, `start` its top-left and `end` its bottom-right; a
 * whole-column range (`A:A`) has no last row (`row: null`).
 */
export interface CellRange {
  start: CellPosition;
  end: { column: number; row: number | null };
}

/** A column's letters: 1 is A, 27 is AA. */
export function columnName(column: number): string {
  let name = "";
  for (let rest = column; rest > 0; rest = Math.floor((rest - 1) / 26))
    name = String.fromCharCode(65 + ((rest - 1) % 26)) + name;
  return name;
}

/** A column's number from its letters, in either case: A is 1. */
export function columnNumber(letters: string): number {
  let column = 0;
  for (const letter of letters.toUpperCase())
    column = column * 26 + (letter.charCodeAt(0) - 64);
  return column;
}

/** A cell's A1 address. */
export function cellAddress({ column, row }: CellPosition): string {
  return `${columnName(column)}${row}`;
}

const ADDRESS = /^([A-Z]{1,3})([1-9][0-9]*)$/;
/** A stored A1 address's position; null when it is not one (addresses are stored in capitals). */
export function parseCellAddress(address: string): CellPosition | null {
  const match = ADDRESS.exec(address);
  if (!match?.[1] || !match[2]) return null;
  return { column: columnNumber(match[1]), row: Number(match[2]) };
}

/** The rectangle two corner cells span, whichever corners they are. */
export function rectangle(
  a: CellPosition,
  b: CellPosition,
): { start: CellPosition; end: CellPosition } {
  return {
    start: {
      column: Math.min(a.column, b.column),
      row: Math.min(a.row, b.row),
    },
    end: { column: Math.max(a.column, b.column), row: Math.max(a.row, b.row) },
  };
}

/** An A1 range's rectangle, whichever corners it names; null when it is not one. */
export function parseCellRange(range: string): {
  start: CellPosition;
  end: CellPosition;
} | null {
  const [first, second, ...rest] = range.split(":");
  const a = first ? parseCellAddress(first) : null;
  const b = second ? parseCellAddress(second) : null;
  return a && b && !rest.length ? rectangle(a, b) : null;
}

/** Whether a cell lies inside a range. */
export function rangeHolds(range: CellRange, cell: CellPosition): boolean {
  return (
    cell.column >= range.start.column &&
    cell.column <= range.end.column &&
    cell.row >= range.start.row &&
    (range.end.row === null || cell.row <= range.end.row)
  );
}

/** Orders cells by row, then column, as a sheet is read. */
export function compareCells(a: CellPosition, b: CellPosition): number {
  return a.row - b.row || a.column - b.column;
}
